import { useMemo, useState } from 'react';
import {
  computeContractSignatoryGateMissing,
  contactDisplayName,
} from '../../utils/nr2jConstants';
import { contractSendGateLabel } from '../../utils/nr2jContractGateUi';
import Nr2jCompleteContactModal from './Nr2jCompleteContactModal.jsx';
import Nr2jCreateContactModal from './Nr2jCreateContactModal.jsx';

export default function Nr2jContractSignatoryField({
  org,
  signatoryContactId,
  onSignatoryContactIdChange,
  onOrgContactsUpdated,
  disabled = false,
  allowCompleteContact = true,
  allowCreateContact = true,
}) {
  const [completeOpen, setCompleteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const selectedSignatory = useMemo(
    () => (org?.contacts || []).find((c) => c.id === signatoryContactId) || null,
    [org, signatoryContactId],
  );

  const signatorySendGateMissing = useMemo(
    () => computeContractSignatoryGateMissing(selectedSignatory),
    [selectedSignatory],
  );

  function mergeContactUpdated(updated) {
    if (!updated?.id || !onOrgContactsUpdated) return;
    onOrgContactsUpdated((prev) => {
      if (!prev) return prev;
      const contacts = (prev.contacts || []).map((c) => (
        c.id === updated.id ? { ...c, ...updated } : c
      ));
      return { ...prev, contacts };
    });
  }

  function appendContactCreated(created) {
    if (!created?.id || !onOrgContactsUpdated) return;
    onOrgContactsUpdated((prev) => {
      if (!prev) return prev;
      const contacts = [...(prev.contacts || []), created];
      return { ...prev, contacts };
    });
    onSignatoryContactIdChange?.(created.id);
  }

  return (
    <>
      <div className="mt-3 block text-sm">
        <span className="text-gray-500">Contact signataire *</span>
        <div className="mt-1 flex flex-wrap items-stretch gap-2">
          <select
            className={`min-w-0 flex-1 rounded-lg border px-3 py-2 disabled:bg-gray-50 ${
              signatorySendGateMissing.length
                ? 'border-amber-400 bg-amber-50/40'
                : 'border-gray-300'
            }`}
            value={signatoryContactId}
            disabled={disabled}
            onChange={(e) => onSignatoryContactIdChange(e.target.value)}
            data-testid="nr2j-contract-signatory"
          >
            <option value="">— Choisir un contact —</option>
            {(org?.contacts || []).map((c) => (
              <option key={c.id} value={c.id}>
                {contactDisplayName(c) || c.id}
                {c.email ? ` (${c.email})` : ''}
                {c.fonction ? ` — ${c.fonction}` : ''}
              </option>
            ))}
          </select>
          {allowCreateContact && org?.id && !disabled ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="shrink-0 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
              data-testid="nr2j-contract-add-contact"
            >
              + Ajouter un contact
            </button>
          ) : null}
        </div>
        {signatorySendGateMissing.length ? (
          <div
            className="mt-2 space-y-2"
            data-testid="nr2j-contract-signatory-send-gate-hint"
          >
            <ul className="space-y-0.5 text-xs text-amber-900">
              {signatorySendGateMissing.map((key) => (
                <li key={key} data-testid={`nr2j-contract-signatory-missing-${key}`}>
                  ⚠ {contractSendGateLabel(key)} — à compléter avant envoi
                </li>
              ))}
            </ul>
            {allowCompleteContact && selectedSignatory && !disabled ? (
              <button
                type="button"
                onClick={() => setCompleteOpen(true)}
                className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-950 hover:bg-amber-100"
                data-testid="nr2j-contract-complete-contact-cta"
              >
                Compléter le contact
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Nr2jCreateContactModal
        open={createOpen}
        organizationId={org?.id}
        onClose={() => setCreateOpen(false)}
        onSaved={(created) => {
          appendContactCreated(created);
          setCreateOpen(false);
        }}
      />

      <Nr2jCompleteContactModal
        open={completeOpen}
        contact={selectedSignatory}
        onClose={() => setCompleteOpen(false)}
        onSaved={(updated) => {
          mergeContactUpdated(updated);
          setCompleteOpen(false);
        }}
      />
    </>
  );
}
